import cn from 'classnames';
import { ReactNode, useMemo, useRef, useState } from 'react';
import * as RadixSelect from '@radix-ui/react-select';

import { Dialog } from '../Dialog';
import { Arrow, Icon } from '../Icon';
import { useIsMobile } from '../utils';

import styles from './Select.module.scss';

export type SelectOption = {
  value: string;
  label: string | JSX.Element;
  icon?: JSX.Element;
};

type SelectProps = {
  options: SelectOption[];
  keyGetter?: (option: SelectOption, index: number) => string | number;
  onChange?: (value: string) => void;
  value?: string;
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  itemClassName?: string;
  labelClassName?: string;
  arrowClassName?: string;
  checkmarkClassName?: string;
  placeholder?: string | ReactNode;
  dropdownAlignedRight?: boolean;
  title?: string | ReactNode;
  mobileMode?: 'dropdown' | 'dialog';
  type?: 'default' | 'extended';
  side?: 'top' | 'right' | 'bottom' | 'left' | undefined;
  showCheckmark?: boolean;
  showOnlyIcon?: boolean;
  hideLabel?: boolean;
  hideArrow?: boolean;
  customArrow?: ReactNode;
};

export function Select({
  options,
  keyGetter = (option, index): string | number =>
    `${option.label.toString()}${index}`,
  value,
  onChange,
  className,
  buttonClassName,
  dropdownClassName,
  itemClassName,
  labelClassName,
  arrowClassName,
  checkmarkClassName,
  placeholder,
  dropdownAlignedRight = false,
  title,
  mobileMode = 'dropdown',
  type = 'default',
  side,
  showCheckmark = false,
  showOnlyIcon = false,
  hideLabel = false,
  hideArrow = false,
  customArrow,
}: SelectProps): JSX.Element {
  const triggerContainer = useRef<HTMLDivElement>(null);

  const selectedItem = options.find((option) => option.value === value);

  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const selectedItemLabel = useMemo(() => {
    if (selectedItem?.label !== undefined) {
      return selectedItem.label;
    }

    if (type === 'extended') {
      return null;
    }

    if (placeholder !== undefined) {
      return placeholder;
    }

    return 'Select item';
  }, [placeholder, selectedItem?.label, type]);

  const items = options.map((item, index) => {
    const isSelected = selectedItem?.value === item.value;
    const key =
      typeof value === 'number' || typeof value === 'string'
        ? value
        : keyGetter(item, index);

    return (
      <div
        key={key}
        className={cn(styles.dropdownItem, {
          [styles.selected]: isSelected,
        })}
        onClick={(): void => {
          onChange?.(item.value);
          setDialogIsOpen(false);
        }}
      >
        <div className={styles.dropdownLabel}>
          {item.icon !== undefined && item.icon}
          <span>{item.label}</span>
        </div>

        <div>
          {isSelected && <Icon icon="select" className={styles.selectedIcon} />}
        </div>
      </div>
    );
  });

  const trigger = (
    <div
      ref={triggerContainer}
      className={cn(
        styles.trigger,
        { [styles.opened]: isOpen },
        styles[type],
        className,
      )}
    >
      {type === 'extended' && (
        <div className={styles.placeholder}>{placeholder}</div>
      )}
      <div className={cn(styles.selectedItemValue, buttonClassName)}>
        {selectedItem?.icon !== undefined && selectedItem?.icon}
        {!showOnlyIcon && (
          <>
            {!hideLabel && selectedItemLabel}

            {customArrow !== null && (
              <div className={styles.arrowContainer}>
                {customArrow === undefined ? (
                  <Arrow className={cn(styles.arrow, arrowClassName)} />
                ) : (
                  customArrow
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (mobileMode === 'dialog' && isMobile) {
    return (
      <div className={className}>
        <Dialog
          open={dialogIsOpen}
          onOpenChange={setDialogIsOpen}
          title={title ?? ''}
          trigger={trigger}
        >
          <div className={styles.dialogContent}>{items}</div>
        </Dialog>
      </div>
    );
  }

  return (
    <RadixSelect.Root
      value={value}
      onValueChange={onChange}
      onOpenChange={setIsOpen}
    >
      <RadixSelect.Trigger asChild>{trigger}</RadixSelect.Trigger>
      <RadixSelect.Portal container={triggerContainer.current}>
        <RadixSelect.Content
          asChild
          align={dropdownAlignedRight ? 'end' : 'start'}
          position="popper"
          side={side}
        >
          <div className={cn(styles.dropdown, dropdownClassName)}>
            <RadixSelect.Viewport className={styles.viewport}>
              {options.map((item, index) => {
                if (item.value === '') return;

                const key =
                  typeof item.value === 'number' ||
                  typeof item.value === 'string'
                    ? item.value
                    : keyGetter(item, index);

                return (
                  <RadixSelect.Item
                    key={key}
                    className={cn(styles.dropdownItem, itemClassName)}
                    value={key.toString()}
                  >
                    <div
                      className={cn(styles.dropdownItemLabel, labelClassName)}
                    >
                      {item.icon !== undefined && item.icon}
                      <RadixSelect.ItemText>{item.label}</RadixSelect.ItemText>
                    </div>

                    {showCheckmark && (
                      <RadixSelect.ItemIndicator className="SelectItemIndicator">
                        <Icon
                          icon="select"
                          className={cn(styles.checkmark, checkmarkClassName)}
                        />
                      </RadixSelect.ItemIndicator>
                    )}
                  </RadixSelect.Item>
                );
              })}
            </RadixSelect.Viewport>
          </div>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
